import React, {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import type { ModalActions } from '@rover/pages/src/bright-horizons/HubPage/components/modals/ModalTypes.common';
import {
  BrightHorizonsAction,
  useApiBrightHorizonsHubScreenRetrieve,
} from '@rover/rsdk/src/apiClient/latest';

export const ModalProviderContext = createContext<ModalActions>({} as ModalActions);

export const ModalProvider: FC<PropsWithChildren> = ({ children }) => {
  const { data } = useApiBrightHorizonsHubScreenRetrieve();
  const [modalAction, setModalAction] = useState<BrightHorizonsAction | null>(null);

  useEffect(() => {
    // If a modalToShow is set, it means we want to show it on page load
    if (data?.modalToShow) {
      setModalAction(data.modalToShow);
    }
  }, [data?.modalToShow]);

  const openModal = (action: BrightHorizonsAction): void => setModalAction(action);

  const closeModal = (): void => setModalAction(null);

  // Context value
  const value = useMemo(
    (): ModalActions => ({
      openModal,
      closeModal,
      modalAction,
    }),
    [modalAction]
  );

  return <ModalProviderContext.Provider value={value}>{children}</ModalProviderContext.Provider>;
};

const useModalProviderContext = (): ModalActions => {
  const context = useContext(ModalProviderContext);
  if (!context) {
    throw new Error('useModalProviderContext must be used within a ModalProvider');
  }
  return context;
};

export default useModalProviderContext;
